import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Video, Building, Clock, Check } from 'lucide-react';
import { format, addDays, parseISO, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();

  const [practitioner, setPractitioner] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/auth?redirect=/booking/${id}`);
      return;
    }

    const fetchData = async () => {
      try {
        const [practResponse, slotsResponse] = await Promise.all([
          axios.get(`${API}/practitioners/${id}`),
          axios.get(`${API}/slots/practitioner/${id}`)
        ]);
        setPractitioner(practResponse.data);
        setSlots(slotsResponse.data);
        
        if (practResponse.data.consultation_types?.length === 1) {
          setConsultationType(practResponse.data.consultation_types[0]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!selectedDate) return;
      
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await axios.get(`${API}/appointments/practitioner/${id}/booked?date=${dateStr}`);
        setBookedSlots(response.data);
      } catch (error) {
        console.error('Error fetching booked slots:', error);
      }
    };
    fetchBookedSlots();
  }, [selectedDate, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !consultationType) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/appointments`,
        {
          practitioner_id: id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          start_time: selectedTime,
          consultation_type: consultationType,
          notes: notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBookingSuccess(true);
      toast.success('Rendez-vous réservé avec succès !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setSubmitting(false);
    }
  };

  // Get available time slots for selected date
  const getAvailableTimeSlots = () => {
    if (!selectedDate || !slots.length) return [];
    
    const dayOfWeek = getDay(selectedDate);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to Monday=0
    
    const daySlots = slots.filter(s => s.day_of_week === adjustedDay);
    const bookedTimes = bookedSlots.map(b => b.start_time);
    
    // Generate time slots from practitioner's availability
    const available = [];
    daySlots.forEach(slot => {
      const [startHour, startMin] = slot.start_time.split(':').map(Number);
      const [endHour, endMin] = slot.end_time.split(':').map(Number);
      const duration = practitioner?.consultation_duration || 60;
      
      let currentHour = startHour;
      let currentMin = startMin;
      
      while (currentHour * 60 + currentMin + duration <= endHour * 60 + endMin) {
        const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
        if (!bookedTimes.includes(timeStr)) {
          available.push(timeStr);
        }
        
        currentMin += duration;
        if (currentMin >= 60) {
          currentHour += Math.floor(currentMin / 60);
          currentMin = currentMin % 60;
        }
      }
    });
    
    return [...new Set(available)].sort();
  };

  // Disable dates without slots
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) return true;
    if (date > addDays(today, 60)) return true;
    
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    return !slots.some(s => s.day_of_week === adjustedDay);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="booking-success">
        <Navbar />
        <div className="container-app py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-semibold mb-4">Rendez-vous confirmé !</h1>
            <p className="text-muted-foreground mb-8">
              Votre rendez-vous avec {practitioner?.user?.first_name} {practitioner?.user?.last_name} 
              le {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })} à {selectedTime} a bien été enregistré.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/client/dashboard">
                <Button className="bg-sage-500 hover:bg-sage-600" data-testid="view-appointments-btn">
                  Voir mes rendez-vous
                </Button>
              </Link>
              <Link to="/search">
                <Button variant="outline" data-testid="search-more-btn">
                  Rechercher d'autres praticiens
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const availableTimeSlots = getAvailableTimeSlots();

  return (
    <div className="min-h-screen bg-background" data-testid="booking-page">
      <Navbar />
      
      <div className="container-app py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au profil
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 md:p-8">
              <h1 className="font-heading text-2xl font-semibold mb-6">
                Réserver avec {practitioner?.user?.first_name} {practitioner?.user?.last_name}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Date Selection */}
                <div>
                  <h2 className="font-medium text-lg mb-4">1. Choisir une date</h2>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setSelectedTime('');
                      }}
                      disabled={isDateDisabled}
                      locale={fr}
                      className="rounded-xl border border-sand-200"
                      data-testid="booking-calendar"
                    />
                  </div>
                </div>

                {/* Time Selection */}
                {selectedDate && (
                  <div className="animate-fadeIn" data-testid="time-selection">
                    <h2 className="font-medium text-lg mb-4">2. Choisir un horaire</h2>
                    {availableTimeSlots.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">
                        Aucun créneau disponible pour cette date
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                        {availableTimeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`time-slot ${selectedTime === time ? 'time-slot-selected' : ''}`}
                            data-testid={`time-slot-${time}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Consultation Type */}
                {selectedTime && practitioner?.consultation_types?.length > 1 && (
                  <div className="animate-fadeIn" data-testid="consultation-type-selection">
                    <h2 className="font-medium text-lg mb-4">3. Type de consultation</h2>
                    <RadioGroup value={consultationType} onValueChange={setConsultationType}>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {practitioner.consultation_types.includes('cabinet') && (
                          <Label
                            htmlFor="cabinet"
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              consultationType === 'cabinet'
                                ? 'border-sage-500 bg-sage-50'
                                : 'border-sand-200 hover:border-sand-300'
                            }`}
                          >
                            <RadioGroupItem value="cabinet" id="cabinet" />
                            <Building className="w-5 h-5 text-sage-600" />
                            <div>
                              <p className="font-medium">En cabinet</p>
                              <p className="text-xs text-muted-foreground">Sur place</p>
                            </div>
                          </Label>
                        )}
                        {practitioner.consultation_types.includes('visio') && (
                          <Label
                            htmlFor="visio"
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              consultationType === 'visio'
                                ? 'border-terracotta-500 bg-terracotta-50'
                                : 'border-sand-200 hover:border-sand-300'
                            }`}
                          >
                            <RadioGroupItem value="visio" id="visio" />
                            <Video className="w-5 h-5 text-terracotta-500" />
                            <div>
                              <p className="font-medium">En visio</p>
                              <p className="text-xs text-muted-foreground">À distance</p>
                            </div>
                          </Label>
                        )}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Notes */}
                {(selectedTime && (consultationType || practitioner?.consultation_types?.length === 1)) && (
                  <div className="animate-fadeIn" data-testid="notes-section">
                    <h2 className="font-medium text-lg mb-4">
                      {practitioner?.consultation_types?.length > 1 ? '4.' : '3.'} Notes (optionnel)
                    </h2>
                    <Textarea
                      placeholder="Décrivez brièvement la raison de votre consultation..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="form-textarea min-h-[100px]"
                      data-testid="booking-notes"
                    />
                  </div>
                )}

                {/* Submit */}
                {selectedDate && selectedTime && (consultationType || practitioner?.consultation_types?.length === 1) && (
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-terracotta-500 hover:bg-terracotta-600 text-white rounded-xl font-medium animate-fadeIn"
                    data-testid="submit-booking"
                  >
                    {submitting ? 'Réservation en cours...' : 'Confirmer le rendez-vous'}
                  </Button>
                )}
              </form>
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-sand-200 shadow-soft p-6 sticky top-24" data-testid="booking-summary">
              <h2 className="font-heading text-lg font-medium mb-4">Récapitulatif</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center">
                    <span className="font-heading font-semibold text-sage-600">
                      {practitioner?.user?.first_name?.[0]}{practitioner?.user?.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{practitioner?.user?.first_name} {practitioner?.user?.last_name}</p>
                    <p className="text-sm text-muted-foreground capitalize">{practitioner?.specialty}</p>
                  </div>
                </div>

                <div className="border-t border-sand-200 pt-4 space-y-3">
                  {selectedDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium">{format(selectedDate, 'd MMMM yyyy', { locale: fr })}</span>
                    </div>
                  )}
                  {selectedTime && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Heure</span>
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                  )}
                  {consultationType && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium capitalize">
                        {consultationType === 'cabinet' ? 'En cabinet' : 'En visio'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Durée</span>
                    <span className="font-medium">{practitioner?.consultation_duration} min</span>
                  </div>
                </div>

                {practitioner?.price && (
                  <div className="border-t border-sand-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total</span>
                      <span className="text-xl font-heading font-semibold text-sage-600">
                        {practitioner.price}€
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Paiement sur place</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
